import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import Header from "./HeaderB";
import ProfileSidebar from "./ProfileSideBar";
import redMarker from "./marker-icon-2x-red.png";
import purpleMarker from "./marker-icon-2x-violet.png";
import markerShadow from "./marker-shadow.png";
import { Navigation, RotateCcw } from "lucide-react";
import { AuthContext } from "../context/AuthContext";

// Fix Leaflet's default marker
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: purpleMarker,
  iconUrl: purpleMarker,
  shadowUrl: markerShadow,
});

// Custom icon for user's location
const userLocationIcon = L.icon({
  iconUrl: redMarker,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const MapEvents = ({ userLocation, storeLocation, mapAction, onActionDone }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !storeLocation) return;

    //controls what we do in the map
    switch (mapAction.type) {
      //when loaded for the first time focus on the store
      case "init":
        map.setView([storeLocation.lat, storeLocation.lng], 18);
        break;
      //when track me focus on the user
      case "zoomUser":
        if (userLocation) {
          map.setView([userLocation.lat, userLocation.lng], 18);
          if (onActionDone) onActionDone();
        }
        break;
      //when reset refocus on the store
      case "reset":
        map.setView([storeLocation.lat, storeLocation.lng], 18);
        if (onActionDone) onActionDone();
        break;
      default:
        break;
    }
  }, [mapAction, userLocation, storeLocation, map, onActionDone]);

  return null;
};

const ShowOnMap = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useContext(AuthContext);

  //get data from the page that called showOnMap
  const {
    lat,
    lng,
    discountTitle,
    storeName,
    city,
    area,
    road,
  } = location.state || {};

  //location info label
  const storeLocationLabel = [road, area, city].filter(Boolean).join(", ");

  const [userLocation, setUserLocation] = useState(null);
  const [shouldZoomToUser, setShouldZoomToUser] = useState(false);
  const [userAreaName, setUserAreaName] = useState(null);

  const [mapAction, setMapAction] = useState({ type: "init" });
  // type: "init" | "zoomUser" | "reset" | "idle"
  const [geoError, setGeoError] = useState("");

  const storeLocation = { lat, lng };

  //Redirect if no coordinates
  if (!lat || !lng) {
    navigate("/favorites");
    return null;
  }

  //locate user's position
  const handleTrackMe = () => {
    setShouldZoomToUser(true);
    setGeoError("");
    if (!navigator.geolocation) {
      setGeoError(t.geoNotSupported);
      setUserAreaName(t.failLoc);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude, accuracy });
        setMapAction({ type: "zoomUser" });
        console.log("User Location:", latitude, longitude);

        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&zoom=16`
          );
          if (!res.ok) throw new Error("Nominatim API request failed");
          const data = await res.json();

          const name =
            data.address?.road ||
            data.address?.neighbourhood ||
            data.address?.suburb ||
            data.address?.village ||
            data.address?.city ||
            data.address?.town ||
            data.display_name ||
            "Άγνωστη περιοχή";

          setUserAreaName(name);
        } catch (error) {
          console.error("Error fetching area name:", error);
          setUserAreaName(t.failLoc);
        }
      },
      (error) => {
        console.error("Error getting location:", error);
        setGeoError(`${t.locError}: ${error.message}`);
        setUserAreaName(t.failLoc);
        setShouldZoomToUser(false);
      },
      { enableHighAccuracy: true, timeout: 60000, maximumAge: 0 }
    );
  };

  // Translations object
  const translations = {
    el: {
      trackMe: "Εντοπισμός θέσης",
      resetView: "Επαναφορά προβολής",
      yourLocation: "Η τοποθεσία σας",
      accuracy: "Ακρίβεια",
      storeLocation: "Τοποθεσία καταστήματος",
      myLocation: "Η τοποθεσία μου",
      geoNotSupported: "Η Geolocation δεν υποστηρίζεται από το πρόγραμμα περιήγησής σας.",
      locError: "Δεν ήταν δυνατή η λήψη της τοποθεσίας",
      unknownArea: "Άγνωστη περιοχή",
      failLoc: "Αποτυχία λήψη θέσης",
      meters: "μ"
    },
    en: {
      trackMe: "Track me",
      resetView: "Reset view",
      yourLocation: "Your location",
      accuracy: "Accuracy",
      storeLocation: "Store location",
      myLocation: "My location",
      geoNotSupported: "Geolocation is not supported by your browser.",
      locError: "Could not get location",
      unknownArea: "Unknown area",
      failLoc: "Location failed",
      meters: "m"
    }
  };

  const t = translations[language] || translations.el;

  return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />
      <div className="flex-1 flex flex-row relative bg-custom-background">
        <ProfileSidebar />
        <main id="main-content" className="flex flex-col flex-1 items-center justify-start px-4 md:px-10 pt-24 pb-10 md:py-10 xl:ml-20 text-center md:text-left">
          <div id="map-content" className="w-full max-w-5xl bg-white/40 rounded-2xl shadow-lg p-6 backdrop-blur-sm">
            <h1 className="sr-only">{discountTitle || storeName}</h1>

            {/* Map */}
            {geoError && (
              <div role="alert" className="mb-4 bg-red-100 text-red-700 p-3 rounded-xl text-sm text-center">
                {geoError}
              </div>
            )}
            <div className="h-[500px] w-full rounded-xl overflow-hidden relative">
              <button
                type="button"
                title={t.trackMe}
                aria-label={t.trackMe}
                onClick={handleTrackMe}
                className="absolute top-4 right-4 z-[1000] p-3 rounded-full bg-custom-header border border-custom-grad1 shadow-lg hover:scale-110 transition-transform active:bg-custom-background focus:outline-none focus:ring-2 focus:ring-custom-grad1 focus:ring-offset-2"
              >
                <Navigation size={20} className="text-custom-grad1" aria-hidden="true" />
              </button>

              <button
                type="button"
                title={t.resetView}
                aria-label={t.resetView}
                onClick={() => setMapAction({ type: "reset" })}
                className="absolute top-16 right-4 z-[1000] p-3 rounded-full bg-custom-header border border-custom-grad1 shadow-lg hover:scale-110 transition-transform active:bg-custom-background focus:outline-none focus:ring-2 focus:ring-custom-grad1 focus:ring-offset-2"
              >
                <RotateCcw size={20} className="text-custom-grad1" aria-hidden="true" />
              </button>
              <p id="map-sr-instructions" className="sr-only">
                {language === "el"
                  ? "Διαδραστικός χάρτης. Εμφανίζει την τοποθεσία του καταστήματος. Χρησιμοποιήστε τα κουμπιά εντοπισμού και επαναφοράς για πλοήγηση."
                  : "Interactive map showing the store location. Use the Track Me and Reset View buttons to navigate."}
              </p>
              <div
                role="application"
                aria-label={language === "el" ? "Χάρτης τοποθεσίας καταστήματος" : "Store location map"}
                aria-describedby="map-sr-instructions"
                style={{ height: "100%", width: "100%" }}
              >
              <MapContainer center={[lat, lng]} zoom={18} style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {/* Store Marker */}
                <Marker position={[lat, lng]}>
                  <Popup>
                    <strong>{storeName}</strong>
                    <br />
                    {storeLocationLabel}
                    <br />
                    {discountTitle}
                  </Popup>
                </Marker>

                {/* User Marker */}
                {userLocation && (
                  <Marker position={[userLocation.lat, userLocation.lng]} icon={userLocationIcon}>
                    <Popup>
                      <strong>{t.yourLocation}</strong>
                      <br />
                      ({t.accuracy}: ±{userLocation.accuracy.toFixed(0)}{t.meters})
                    </Popup>
                  </Marker>
                )}

                <MapEvents
                  userLocation={userLocation}
                  storeLocation={storeLocation}
                  mapAction={mapAction}
                  onActionDone={() => setMapAction({ type: "idle" })}
                />
              </MapContainer>
              </div>
            </div>

            {/* Info */}
            <section aria-label={language === "el" ? "Πληροφορίες τοποθεσίας" : "Location information"} className="text-center text-custom-letters mt-4">
              <p className="font-semibold text-lg">{storeName}</p>
              {storeLocationLabel && (
                <p className="text-sm text-custom-letters mb-2">
                  <span className="sr-only">{t.storeLocation}: </span>{storeLocationLabel}
                </p>
              )}
              {userAreaName && (
                <p className="text-base font-medium text-custom-grad1">
                  <span className="sr-only">{t.myLocation}: </span>{userAreaName}
                </p>
              )}
            </section>

          </div>
        </main>
      </div>
    </div>
  );
};

export default ShowOnMap;
